import type { SyntheticEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import copy from 'copy-to-clipboard';

import {
  BpkButtonV2,
  BUTTON_TYPES,
} from '@skyscanner/backpack-web/bpk-component-button';
import ShareIcon from '@skyscanner/backpack-web/bpk-component-icon/lg/share--ios';
import BpkLink, {
  BpkButtonLink,
} from '@skyscanner/backpack-web/bpk-component-link';
import BpkPopover from '@skyscanner/backpack-web/bpk-component-popover';
import BpkText from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { I18nService } from '@shared/types/i18n';
import type { Optional } from '@shared/types/utils';

import STYLES from './SaveAndShare.module.scss';

import { useHotelBasicInfo } from '@hotels-hotel-detail/data-access/hooks/useHotelContent';
import {
  useLowestPrice,
  usePriceContext,
  useStay,
} from '@hotels-hotel-detail/data-access/hooks/useHotelOffersContext';
import {
  appendUTMFlag,
  getCurrentUrl,
  SHARE_LINK,
  SHARE_LINK_MAPPING,
  SHARE_ITEMS,
  linkImage,
} from '@hotels-hotel-detail/utils/hotel-content';
import SaveButton from '@shared/ui/components/SaveButton';
import { withI18n } from '@shared/ui/contexts/i18n';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';
import {
  ACTION_TYPE,
  CLICK_AREA,
} from '@shared/utils-frontend/mini-events/constants';
import { buildAdditionalInfoViewButtonClicked } from '@shared/utils-frontend/mini-events/hotels-action';

const cls = cssModules(STYLES);

type Props = {
  i18n: I18nService;
  miniEventTracker: MiniEventTracker;
};

const SaveAndShare = ({ i18n, miniEventTracker }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const shareTarget = useRef(null);
  const intervalId = useRef<Optional<ReturnType<typeof setTimeout>>>(undefined);

  useEffect(
    () => () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
    },
    [],
  );

  const onOpen = () => {
    setIsOpen(true);
  };

  const copyCurrentUrl = () => {
    const copyUrl = appendUTMFlag(getCurrentUrl(), 'direct', i18n);
    copy(copyUrl);
    setLinkCopied(true);
    onShareLinkClick(SHARE_LINK.DETAIL);
    intervalId.current = setTimeout(() => {
      setLinkCopied(false);
    }, 3000);
  };

  const onShareLinkClick = (type: string) => {
    miniEventTracker.trackHotelsAction(
      ACTION_TYPE.VIEW_BUTTON_CLICKED,
      buildAdditionalInfoViewButtonClicked(SHARE_LINK_MAPPING[type]),
    );
  };

  const onClose = (e: SyntheticEvent<HTMLButtonElement, Event> | null) => {
    e?.stopPropagation();
    setIsOpen(false);
  };

  const uBffCorrelationId = usePriceContext().correlationId;
  const uBffStay = useStay();
  const uBffPrice = useLowestPrice().lowestPriceForTraveller;
  const { hotelId } = useHotelBasicInfo();

  return (
    <div className={cls('SaveAndShare')}>
      <BpkPopover
        id="share-popover"
        target={
          <div ref={shareTarget}>
            <BpkButtonV2
              iconOnly
              type={BUTTON_TYPES.link}
              className={cls('SaveAndShare__sharePopover')}
              onClick={onOpen}
              aria-label={i18n.translate('DealsPage_Share_colon')}
              data-testid="share-link"
            >
              <div className={cls('SaveAndShare__share--save')}>
                <ShareIcon className={cls('SaveAndShare__shareIcon--save')} />
              </div>
            </BpkButtonV2>
          </div>
        }
        onClose={onClose}
        isOpen={isOpen}
        label="hotel-detail-share-label"
        closeButtonText={i18n.translate('Modal_close')}
        closeButtonProps={{
          tabIndex: 0,
        }}
      >
        {SHARE_ITEMS.map(({ img, text, type, url }) => (
          <BpkLink
            implicit
            key={type}
            className={cls('SaveAndShare__shareLink')}
            href={appendUTMFlag(url, type, i18n)}
            onClick={() => onShareLinkClick(type)}
            target="_blank"
            data-testid="share-items"
          >
            <img src={img} alt="" />
            <BpkText className={cls('SaveAndShare__copyMargin')}>
              {i18n.translate(text)}
            </BpkText>
          </BpkLink>
        ))}
        <div className={cls('SaveAndShare__shareLink')}>
          <img src={linkImage} alt="" />
          <BpkText className={cls('SaveAndShare__copyMargin')}>
            {i18n.translate('HotelDetail_label_sharelink_desc')}
          </BpkText>
          <BpkButtonLink
            className={cls('SaveAndShare__copyMargin')}
            onClick={() => copyCurrentUrl()}
          >
            {i18n.translate('HotelDetail_label_copylink')}
          </BpkButtonLink>
        </div>
        <div className={cls('SaveAndShare__linkToastBox')}>
          {linkCopied && (
            <div className={cls('SaveAndShare__linkToast')}>
              <BpkText tagName="p">
                {i18n.translate('HotelDetail_label_sharelink_toast')}
              </BpkText>
            </div>
          )}
        </div>
      </BpkPopover>
      <div className={cls('SaveAndShare__saveButton')}>
        <SaveButton
          id={hotelId}
          stay={uBffStay}
          price={uBffPrice}
          clickArea={CLICK_AREA.DETAIL_SAVE_ICON}
          correlationId={uBffCorrelationId}
        />
      </div>
    </div>
  );
};

export default withMiniEventTracker(withI18n(SaveAndShare));
